import './ManualRecognitionBadge.css';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * A8r Slice D — ManualRecognitionBadge.
 *
 * Small "[GM]" marker rendered next to an NPC's name when their recognition
 * state is currently GM-overridden (gm_override !== null on the backend
 * fame entry). Pure cosmetic surface — the underlying override state is
 * unaffected by this component.
 *
 * Consumers:
 *   - HistoryPanel TAROT Identification rows (next to NPC name)
 *   - FameRecognitionCinematic reveal pane (when fresh roll's gm_override
 *     was set by GM mid-cinematic — rare but possible)
 *   - Dossier exporter (future surface)
 *
 * A11y: explicit `aria-label="Recognition set by Game Master"` so screen
 * readers convey the meaning beyond the visual marker. Inline text
 * `[GM]` (NOT a CSS-only dot) so the badge is perceivable by every
 * accessibility tier (including high-contrast users where rgba colors
 * may not render).
 *
 * Atmosphere flag: `manualBadgeVisibleEnabled` (default true; minimal
 * preset true — security/transparency feature). When the flag is off,
 * the badge renders nothing.
 */
export interface ManualRecognitionBadgeProps {
  /** When true, the badge will render (subject to the atmosphere flag).
   *  Drive this from useFameRecognition().manuallyOverriddenIds.has(npcId). */
  visible: boolean;
  /** NPC name for aria-label personalization. Optional — falls back to
   *  a generic label when omitted. */
  npcName?: string;
  /** Test id override; default `br-manual-recognition-badge`. */
  testid?: string;
}

export function ManualRecognitionBadge({
  visible,
  npcName,
  testid = 'br-manual-recognition-badge',
}: ManualRecognitionBadgeProps) {
  const { manualBadgeVisibleEnabled } = useAtmosphereSettings();
  if (!visible || !manualBadgeVisibleEnabled) {
    return null;
  }
  const ariaLabel = npcName
    ? `${npcName} recognition set by Game Master`
    : 'Recognition set by Game Master';
  return (
    <span
      className="br-manual-recognition-badge"
      data-testid={testid}
      role="note"
      aria-label={ariaLabel}
      title="Recognition state was set by the GM (not from the natural roll)"
    >
      [GM]
    </span>
  );
}

export default ManualRecognitionBadge;
